/**
 * Fuldskærms-statusskærme for konto/firma:
 *   - profil: pending / inactive / suspended
 *   - firma: suspended / inactive (vises med sælgerkontaktkort)
 * Firmastatus har forrang: er firmaet suspenderet/deaktiveret, vises det
 * uanset hvilken profilstatus der blev sendt ind.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Building2, Clock, LogOut, Mail, Phone, UserX } from "lucide-react";
import { supabase } from "@/lib/supabase/client";
import { callFunction } from "@/lib/functions";
import { logger } from "@/lib/logger";
import { useAuth } from "@/features/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { FullPageSpinner } from "@/components/layout/FullPageSpinner";

interface Props {
  status: "pending" | "inactive" | "suspended";
}

interface SalespersonProfile {
  name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
}

function SalespersonContactCard({ salesperson }: { salesperson: SalespersonProfile | null }) {
  if (!salesperson) return null;
  const fullName = [salesperson.name, salesperson.last_name].filter(Boolean).join(" ");
  return (
    <Card className="w-full">
      <CardContent className="flex items-center gap-4 p-4">
        <Avatar className="h-12 w-12">
          <AvatarImage src={salesperson.avatar_url ?? undefined} alt="" />
          <AvatarFallback>{(salesperson.name ?? "?").slice(0, 1).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 text-left">
          <p className="font-semibold">{fullName || "Din kontaktperson"}</p>
          {salesperson.phone && (
            <a
              href={`tel:${salesperson.phone}`}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:underline"
            >
              <Phone className="h-3.5 w-3.5" aria-hidden /> {salesperson.phone}
            </a>
          )}
          {salesperson.email && (
            <a
              href={`mailto:${salesperson.email}`}
              className="flex items-center gap-1.5 truncate text-sm text-muted-foreground hover:underline"
            >
              <Mail className="h-3.5 w-3.5" aria-hidden /> {salesperson.email}
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Screen({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  const { signOut } = useAuth();
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-4">
      <div className="flex w-full max-w-md flex-col items-center gap-4 text-center">
        {icon}
        <h1 className="text-2xl font-bold">{title}</h1>
        {children}
        <Button variant="outline" className="min-h-11" onClick={() => void signOut()}>
          <LogOut className="mr-2 h-4 w-4" aria-hidden />
          Log ud
        </Button>
      </div>
    </div>
  );
}

/**
 * Pending: prøv automatisk godkendelse (gyldigt dansk CVR = adgang med det
 * samme). Lykkes den, genindlæses appen; ellers vises ventebeskeden og
 * ansøgningen behandles manuelt som hidtil.
 */
function PendingScreen() {
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await callFunction<{ approved: boolean }>("signup-auto-approve", {}, { timeoutMs: 20_000 });
        if (!cancelled && res.approved) {
          window.location.reload();
          return;
        }
      } catch (e) {
        logger.warn("auto_approve_fejlede", { error: String(e) });
      }
      if (!cancelled) setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (checking) return <FullPageSpinner />;
  return (
    <Screen
      icon={<Clock className="h-14 w-14 text-primary" aria-hidden />}
      title="Konto afventer godkendelse"
    >
      <p className="text-muted-foreground">
        Tak for din ansøgning! Vi gennemgår den hurtigst muligt — du hører typisk fra os
        inden for 1-2 hverdage. Du får en e-mail, når kontoen er klar.
      </p>
    </Screen>
  );
}

export function StatusScreen({ status }: Props) {
  const { profile } = useAuth();

  const companyQuery = useQuery({
    queryKey: ["statusskaerm-firma", profile?.company_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("id, name, status, salesperson_user_id")
        .eq("id", profile?.company_id as string)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.company_id,
  });

  const company = companyQuery.data;
  const salespersonQuery = useQuery({
    queryKey: ["statusskaerm-saelger", company?.salesperson_user_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("name, last_name, email, phone, avatar_url")
        .eq("user_id", company?.salesperson_user_id as string)
        .maybeSingle();
      if (error) throw error;
      return data as SalespersonProfile | null;
    },
    enabled: !!company?.salesperson_user_id,
  });

  if (profile?.company_id && companyQuery.isLoading) return <FullPageSpinner />;

  // Firmastatus har forrang
  if (company?.status === "suspended") {
    return (
      <Screen
        icon={<AlertTriangle className="h-14 w-14 text-destructive" aria-hidden />}
        title="Konto suspenderet"
      >
        <p className="text-muted-foreground">
          Adgangen for {company.name} er midlertidigt suspenderet. Kontakt din tilknyttede
          sælger for at få genåbnet kontoen.
        </p>
        <SalespersonContactCard salesperson={salespersonQuery.data ?? null} />
      </Screen>
    );
  }
  if (company?.status === "inactive") {
    return (
      <Screen
        icon={<Building2 className="h-14 w-14 text-muted-foreground" aria-hidden />}
        title="Firma deaktiveret"
      >
        <p className="text-muted-foreground">
          {company.name} er ikke længere aktivt i LAB3D B2B-portalen. Kontakt din sælger,
          hvis det er en fejl.
        </p>
        <SalespersonContactCard salesperson={salespersonQuery.data ?? null} />
      </Screen>
    );
  }

  if (status === "pending") {
    return <PendingScreen />;
  }

  if (status === "suspended") {
    return (
      <Screen
        icon={<AlertTriangle className="h-14 w-14 text-destructive" aria-hidden />}
        title="Konto suspenderet"
      >
        <p className="text-muted-foreground">
          Din konto er midlertidigt suspenderet. Kontakt LAB3D på{" "}
          <a href="mailto:shop@lab3d.dk" className="underline">
            shop@lab3d.dk
          </a>{" "}
          for at få hjælp.
        </p>
        <SalespersonContactCard salesperson={salespersonQuery.data ?? null} />
      </Screen>
    );
  }

  return (
    <Screen
      icon={<UserX className="h-14 w-14 text-muted-foreground" aria-hidden />}
      title="Konto deaktiveret"
    >
      <p className="text-muted-foreground">
        Din konto er deaktiveret. Kontakt LAB3D på{" "}
        <a href="mailto:shop@lab3d.dk" className="underline">
          shop@lab3d.dk
        </a>
        , hvis du mener, det er en fejl.
      </p>
    </Screen>
  );
}

/**
 * Vagt til AppLayout: en AKTIV profil i et suspenderet/deaktiveret firma skal
 * også mødes af statusskærmen (ProtectedRoute tjekker kun profilstatus).
 * Gælder kun brugerens EGET firma — kundevisning påvirkes ikke.
 */
export function CompanyStatusGate({ children }: { children: ReactNode }) {
  const { profile, isSalesperson } = useAuth();

  const companyQuery = useQuery({
    queryKey: ["statusskaerm-firma", profile?.company_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select("id, name, status, salesperson_user_id")
        .eq("id", profile?.company_id as string)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.company_id && !isSalesperson,
  });

  if (
    !isSalesperson &&
    profile?.status === "active" &&
    (companyQuery.data?.status === "suspended" || companyQuery.data?.status === "inactive")
  ) {
    return <StatusScreen status="suspended" />;
  }
  return <>{children}</>;
}
